import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Sparkles, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { useStore } from "@/context/StoreContext";
import { useCart } from "@/context/CartContext";
import StoreShell from "@/components/StoreShell";
import ToneImg from "@/components/storefront/ToneImg";
import { Reveal, CountUp } from "@/components/pharmacy/hooks";
import { ManagedReviews, ManagedInstagram } from "@/components/storefront/HomeSections";
import { Z, ZTitle, ZButton, ArtisanCard, Stars, needsOptions, spaced } from "@/components/artisan/ArtisanParts";
import { homeSets } from "@/lib/homesets";
import { inr } from "@/lib/storeutils";

const wrap = "max-w-[1320px] mx-auto px-5";
const uniq = (list) => { const seen = new Set(); return list.filter((p) => p && !seen.has(p.id) && seen.add(p.id)); };

export default function ArtisanHome() {
  const s = useStore();
  const cart = useCart();
  const [params] = useSearchParams();
  const q = (params.get("q") || "").toLowerCase();
  useEffect(() => { if (q && !s.loading) document.getElementById("shop")?.scrollIntoView({ behavior: "smooth" }); }, [q, s.loading]);

  const all = s.products;
  const sets = useMemo(() => homeSets(all, { fresh: 8, featured: 8, sale: 4 }), [all]);
  if (s.loading || !s.store) return <div className="min-h-screen flex items-center justify-center"><span className="w-10 h-10 rounded-full border-4 border-amber-100 border-t-amber-600 animate-spin" /></div>;
  if (s.error) return <div className="min-h-screen flex items-center justify-center text-slate-500">{s.error}</div>;

  const { slug, store } = s;
  const shown = all.filter((p) => `${p.name} ${p.category || ""}`.toLowerCase().includes(q));
  if (q) {
    return (
      <StoreShell>
        <section id="shop" className={`${wrap} py-12`} data-testid="artisan-search-results">
          <ZTitle eyebrow="Search">Results for “{q}”</ZTitle>
          {shown.length === 0 ? <div className="text-center py-16" style={{ color: Z.muted }}>No products found.</div> : (
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-x-5 gap-y-9" data-testid="storefront-products">{shown.map((p) => <ArtisanCard key={p.id} p={p} s={s} cart={cart} />)}</div>
          )}
        </section>
      </StoreShell>
    );
  }

  const withImg = all.filter((p) => p.image);
  const ranked = uniq([...sets.featured, ...sets.fresh, ...sets.sale, ...withImg]).filter((p) => p.image);
  const pick = (i) => ranked[i % Math.max(ranked.length, 1)];
  const cats = s.categories.map((name) => ({ name, count: all.filter((p) => p.category === name).length, image: all.find((p) => p.category === name && p.image)?.image }));
  const picks = sets.featured.length > 1 ? sets.featured : sets.fresh.length ? sets.fresh : all.slice(0, 8);
  const pool = uniq([...picks, ...all]);

  return (
    <StoreShell>
      <ChipRail s={s} cats={cats} />
      <Hero s={s} slides={ranked.slice(0, 3)} />
      <Collections s={s} cats={cats} fallback={ranked} />
      <Story s={s} count={all.length} cats={cats.length} photos={[pick(1), pick(2), pick(3)]} />
      <MotionBand photos={ranked.slice(0, 12)} />
      {picks.length > 0 && (
        <section id="featured" className={`${wrap} py-16 md:py-20`}>
          <Reveal><ZTitle eyebrow="Loved by our customers">Top picks from <em style={{ color: Z.primary }}>{store.name}</em></ZTitle></Reveal>
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-x-4 md:gap-x-6 gap-y-10" data-testid="storefront-products">
            {picks.slice(0, 8).map((p, k) => <Reveal key={p.id} delay={(k % 4) * 90} kind="rv-zoom"><ArtisanCard p={p} s={s} cart={cart} /></Reveal>)}
          </div>
          <Reveal className="mt-12 flex justify-center"><ZButton to={`/store/${slug}/shop`} variant="ghost">View all</ZButton></Reveal>
        </section>
      )}
      <TextMarquee />
      {ranked.length > 0 && <ShopLook s={s} cart={cart} scenes={ranked.slice(0, 3)} pool={pool} />}
      <ManagedReviews s={s} layout="artisan" />
      <ManagedInstagram s={s} layout="artisan" />
    </StoreShell>
  );
}

/* --------------------------------------------------------- category chips */
function ChipRail({ s, cats }) {
  if (!cats.length) return null;
  return (
    <section className="border-b" style={{ borderColor: Z.line, background: Z.cream }} data-testid="artisan-chips">
      <div className={`${wrap} py-4 flex gap-5 md:gap-7 overflow-x-auto`} style={{ scrollbarWidth: "none" }}>
        {cats.map((c, k) => (
          <Reveal key={c.name} delay={k * 50} className="shrink-0">
            <Link to={`/store/${s.slug}/shop?category=${encodeURIComponent(c.name)}`} data-testid={`artisan-chip-${c.name}`} className="zn-chip group flex flex-col items-center gap-2 w-[78px]">
              <span className="zn-chip-ring relative w-[62px] h-[62px] rounded-full p-[3px]" style={{ background: `conic-gradient(from 0deg, ${Z.accent}, ${Z.primary}, ${Z.accent})` }}>
                <span className="block w-full h-full rounded-full overflow-hidden border-2 border-white" style={{ background: Z.sand }}>{c.image ? <ToneImg src={c.image} loading="lazy" imgClassName="zn-zoom" /> : <Sparkles size={20} className="m-auto mt-4" style={{ color: Z.primary }} />}</span>
              </span>
              <span className="text-[10.5px] leading-tight text-center line-clamp-2 transition-colors group-hover:text-[color:var(--z-primary,#8c2f39)]" style={{ color: Z.ink }}>{c.name}</span>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------- hero */
const GRAIN = "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.35  0 0 0 0 0.2  0 0 0 0 0.1  0 0 0 .08 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")";

function Words({ items, delay = 0 }) {
  return items.map(([w, em], k) => (
    <span key={k} className="inline-block overflow-hidden align-bottom pr-[.22em]"><span className="zn-word inline-block" style={{ animationDelay: `${delay + k * 90}ms`, ...(em ? { fontStyle: "italic", color: Z.primary } : null) }}>{w}</span></span>
  ));
}

function Hero({ s, slides }) {
  const { slug, store } = s;
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const root = useRef(null);
  useEffect(() => { if (paused || slides.length < 2) return undefined; const t = setInterval(() => setI((x) => (x + 1) % slides.length), 5600); return () => clearInterval(t); }, [paused, slides.length]);
  useEffect(() => {
    const el = root.current; if (!el) return undefined;
    const f = () => el.style.setProperty("--py", `${Math.min(window.scrollY, 700) * 0.12}px`);
    f(); window.addEventListener("scroll", f, { passive: true }); return () => window.removeEventListener("scroll", f);
  }, []);
  const cur = slides[i];
  return (
    <section ref={root} className="relative overflow-hidden" style={{ background: `linear-gradient(120deg, ${Z.cream} 0%, ${Z.sand} 60%, #ecd7b8 100%)` }} data-testid="artisan-hero" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: GRAIN, opacity: .9 }} />
      <span className="zn-blob absolute -left-24 top-10 w-80 h-80 rounded-full" style={{ background: "rgba(140,47,57,.08)" }} />
      <span className="zn-blob absolute right-[30%] -bottom-32 w-96 h-96 rounded-full" style={{ background: "rgba(224,160,48,.16)", animationDelay: "-4s" }} />
      <div className={`${wrap} relative grid lg:grid-cols-[1.05fr_.95fr] gap-10 items-center py-12 md:py-20 min-h-[560px]`}>
        <div className="relative z-10 text-center lg:text-left">
          <div className="zn-rise inline-flex items-center gap-2 text-[11px] font-semibold uppercase" style={{ ...spaced, color: Z.primary }}><Sparkles size={14} /> Handcrafted · small batch</div>
          <h1 className="mt-5 text-[44px] sm:text-[58px] lg:text-[76px] leading-[1.02]" style={{ fontFamily: Z.serif, fontWeight: 500, color: Z.ink }}>
            <Words items={[["Crafted", 0], ["by", 0], ["hand,", 0]]} /><br />
            <Words items={[["cherished", 0], ["for", 0], ["years.", 1]]} delay={300} />
          </h1>
          <p className="zn-rise mt-6 text-[15px] md:text-[16px] leading-relaxed max-w-md mx-auto lg:mx-0" style={{ color: Z.muted, animationDelay: ".7s" }}>One-of-a-kind pieces from {store.name}, made by skilled artisans. Every item carries a story, and a little of the hands that made it.</p>
          <div className="zn-rise mt-8 flex flex-wrap items-center gap-4 justify-center lg:justify-start" style={{ animationDelay: ".85s" }}>
            <ZButton to={`/store/${slug}/shop`} testId="artisan-hero-cta">Shop bestsellers</ZButton>
            <a href="#collections" className="text-[12px] font-semibold uppercase underline underline-offset-8 decoration-2 transition-colors hover:text-[color:var(--z-primary,#8c2f39)]" style={{ letterSpacing: ".14em", color: Z.ink, textDecorationColor: Z.accent }}>Browse collections</a>
          </div>
        </div>
        <div className="relative z-10 flex justify-center">
          <div className="relative w-[min(430px,86%)]" style={{ transform: "translateY(calc(var(--py, 0px) * -1))" }}>
            <div aria-hidden className="absolute -inset-3 rounded-t-[999px] rounded-b-[36px] border-2 zn-arch-ring" style={{ borderColor: Z.accent }} />
            <div className="relative aspect-[4/5] rounded-t-[999px] rounded-b-[32px] overflow-hidden shadow-[0_30px_70px_rgba(90,40,20,.28)]" style={{ background: Z.sand }}>
              {slides.map((p, k) => (
                <div key={p.id} className="absolute inset-0 transition-opacity duration-1000" style={{ opacity: k === i ? 1 : 0 }}>
                  <ToneImg src={p.image} alt={p.name} loading="eager" imgClassName={k === i ? "zn-kenburns" : ""} />
                </div>
              ))}
            </div>
            {cur && (
              <Link to={`/store/${slug}/product/${cur.id}`} key={cur.id} className="zn-in absolute -left-6 md:-left-14 bottom-10 flex items-center gap-3 rounded-2xl bg-white/95 backdrop-blur px-3.5 py-3 shadow-xl max-w-[250px] zn-float">
                <span className="w-12 h-12 rounded-xl overflow-hidden shrink-0" style={{ background: Z.sand }}><ToneImg src={cur.image} /></span>
                <span className="min-w-0"><span className="block text-[12.5px] leading-snug line-clamp-2" style={{ color: Z.ink }}>{cur.name}</span><span className="block text-[13px] font-semibold" style={{ color: Z.primary }}>{inr(cur.price)}</span></span>
              </Link>
            )}
            <svg aria-hidden viewBox="0 0 120 120" className="zn-spin-slow absolute -right-5 md:-right-10 -top-4 w-[104px] md:w-[124px]">
              <defs><path id="znc" d="M60,60 m-45,0 a45,45 0 1,1 90,0 a45,45 0 1,1 -90,0" /></defs>
              <circle cx="60" cy="60" r="58" fill={Z.primary} />
              <text fontSize="10" fontWeight="600" fill="#fff"><textPath href="#znc" textLength="276" lengthAdjust="spacing">HANDMADE WITH LOVE • ONE OF A KIND •</textPath></text>
              <path d="M60 46 L63.5 56.5 L74 60 L63.5 63.5 L60 74 L56.5 63.5 L46 60 L56.5 56.5Z" fill={Z.accent} />
            </svg>
          </div>
        </div>
      </div>
      {slides.length > 1 && (
        <div className="absolute left-0 right-0 bottom-5 flex justify-center gap-2.5">
          {slides.map((p, k) => (
            <button key={p.id} type="button" aria-label={`Slide ${k + 1}`} onClick={() => setI(k)} className="relative h-1.5 rounded-full overflow-hidden transition-all" style={{ width: k === i ? 44 : 12, background: "rgba(42,29,26,.18)" }}>
              {k === i && <span key={`${i}-${paused}`} className="zn-progress absolute inset-y-0 left-0 rounded-full" style={{ background: Z.primary, animationPlayState: paused ? "paused" : "running" }} />}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

/* ------------------------------------------------------ bento collections */
const BENTO = ["col-span-2 lg:col-span-2 lg:row-span-2", "", "lg:row-span-2", "", "", "col-span-2 lg:col-span-2", ""];

function Collections({ s, cats, fallback }) {
  const { slug } = s;
  const enough = cats.filter((c) => c.image);
  const tiles = (enough.length >= 3 ? enough.map((c) => ({ key: c.name, name: c.name, sub: `${c.count} piece${c.count === 1 ? "" : "s"}`, image: c.image, to: `/store/${slug}/shop?category=${encodeURIComponent(c.name)}` }))
    : fallback.slice(0, 7).map((p) => ({ key: p.id, name: p.name, sub: p.category || "Handcrafted", image: p.image, to: `/store/${slug}/product/${p.id}` })));
  const n = tiles.length >= 7 ? 7 : tiles.length >= 4 ? 4 : tiles.length;
  const list = tiles.slice(0, n);
  if (list.length < 2) return null;
  const grid = n >= 4 ? "grid-cols-2 lg:grid-cols-4 auto-rows-[190px] md:auto-rows-[240px] lg:auto-rows-[230px]" : "grid-cols-1 sm:grid-cols-3 auto-rows-[260px]";
  return (
    <section id="collections" className={`${wrap} py-16 md:py-20`}>
      <Reveal><ZTitle eyebrow="Shop by craft">Statement <em style={{ color: Z.primary }}>collections</em></ZTitle></Reveal>
      <div className={`mt-12 grid gap-3 md:gap-4 ${grid}`}>
        {list.map((t, k) => (
          <Reveal key={t.key} delay={k * 80} kind="rv-zoom" className={n >= 4 ? BENTO[k] : ""}>
            <Link to={t.to} data-testid={`artisan-collection-${k}`} className="zn-tile group relative block w-full h-full rounded-[22px] md:rounded-[28px] overflow-hidden" style={{ background: Z.sand }}>
              <ToneImg src={t.image} alt="" loading="lazy" imgClassName="zn-zoom" />
              <span className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(30,14,10,.86) 0%, rgba(30,14,10,.55) 32%, rgba(30,14,10,0) 68%)" }} />
              <span className="absolute left-4 right-4 bottom-4 md:left-6 md:right-6 md:bottom-6 flex items-end justify-between gap-3 text-white">
                <span className="min-w-0 transition-transform duration-500 group-hover:-translate-y-1">
                  <span className="block text-[18px] md:text-[24px] leading-tight line-clamp-2" style={{ fontFamily: Z.serif, fontWeight: 500, textShadow: "0 2px 12px rgba(0,0,0,.45)" }}>{t.name}</span>
                  <span className="block text-[11px] uppercase mt-1 opacity-85" style={{ letterSpacing: ".18em" }}>{t.sub}</span>
                </span>
                <span className="shrink-0 w-10 h-10 md:w-11 md:h-11 rounded-full bg-white flex items-center justify-center -rotate-45 transition-transform duration-500 group-hover:rotate-0" style={{ color: Z.ink }}><ArrowRight size={17} /></span>
              </span>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------ artisan story */
function Story({ s, count, cats, photos }) {
  const stats = [[Math.max(count, 1), "+", "Handcrafted pieces"], [Math.max(cats, 1), "", "Collections"], [100, "%", "Made by hand"]];
  return (
    <section className="relative overflow-hidden" style={{ background: Z.sand }}>
      <div className={`${wrap} py-16 md:py-24 grid lg:grid-cols-2 gap-14 items-center`}>
        <Reveal kind="rv-left">
          <div className="flex items-center gap-3 text-[11px] uppercase font-semibold" style={{ ...spaced, color: Z.primary }}><span className="h-px w-10" style={{ background: "currentColor" }} />Our promise</div>
          <h2 className="mt-4 text-[34px] md:text-[52px] leading-[1.08]" style={{ fontFamily: Z.serif, fontWeight: 500, color: Z.ink }}>Crafted with love by <em style={{ color: Z.primary }}>skilled artisans</em></h2>
          <p className="mt-5 text-[15px] leading-relaxed max-w-lg" style={{ color: Z.muted }}>Every piece at {s.store.name} is handcrafted by makers who have practised their craft for generations. By choosing handmade you are not just elevating your home, you are helping keep traditional crafts and the communities behind them alive.</p>
          <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
            {stats.map(([n, suf, label]) => (
              <div key={label} className="border-l-2 pl-4" style={{ borderColor: Z.accent }}>
                <div className="text-[30px] md:text-[38px] leading-none" style={{ fontFamily: Z.serif, fontWeight: 500, color: Z.primary }}><CountUp to={n} suffix={suf} /></div>
                <div className="mt-1.5 text-[11px] uppercase leading-snug" style={{ letterSpacing: ".1em", color: Z.muted }}>{label}</div>
              </div>
            ))}
          </div>
          <div className="mt-9"><ZButton to={`/store/${s.slug}/about`}>Our story</ZButton></div>
        </Reveal>
        <Reveal kind="rv-right" className="relative h-[360px] sm:h-[460px]">
          {[[photos[0], "6%", "4%", 56, -6, 0], [photos[1], "46%", "18%", 50, 5, 1.1], [photos[2], "16%", "52%", 46, -3, 2.2]].map(([p, l, t, w, rot, d], i) => p && (
            <div key={i} className="zn-float absolute rounded-[26px] overflow-hidden border-[6px] border-white shadow-2xl" style={{ left: l, top: t, width: `${w}%`, aspectRatio: i === 1 ? "3/4" : "1", transform: `rotate(${rot}deg)`, animationDelay: `${d}s` }}><ToneImg src={p.image} loading="lazy" /></div>
          ))}
          <span aria-hidden className="zn-spin-slow absolute right-2 bottom-2 w-24 h-24 rounded-full flex items-center justify-center" style={{ background: Z.accent, color: Z.ink }}><Sparkles size={34} /></span>
        </Reveal>
      </div>
    </section>
  );
}

/* ----------------------------------------------------------- craft in motion */
function MotionBand({ photos }) {
  if (photos.length < 3) return null;
  const row = (list, rev) => (
    <div className="flex overflow-hidden">
      <div className={`flex gap-4 shrink-0 pr-4 ${rev ? "zn-scroll-rev" : "zn-scroll"}`} aria-hidden={rev}>
        {[...list, ...list].map((p, k) => (
          <div key={`${p.id}-${k}`} className="w-[170px] h-[210px] md:w-[220px] md:h-[270px] shrink-0 rounded-[24px] overflow-hidden" style={{ background: "#3b1d20" }}><ToneImg src={p.image} loading="lazy" /></div>
        ))}
      </div>
    </div>
  );
  const half = Math.ceil(photos.length / 2);
  return (
    <section className="relative overflow-hidden py-16 md:py-24" style={{ background: "linear-gradient(180deg,#2a1416,#3b1d20)" }}>
      <Reveal className="px-5"><ZTitle eyebrow="Craft in motion" light>Made by hand, <em style={{ color: Z.accent }}>one piece</em> at a time</ZTitle></Reveal>
      <div className="mt-12 space-y-4 zn-pause">{row(photos.slice(0, half), false)}{row(photos.slice(half).concat(photos.slice(0, 2)), true)}</div>
    </section>
  );
}

/* ---------------------------------------------------------------- text strip */
function TextMarquee() {
  const items = ["Cherished in your home", "Crafted by artisans", "Made with love", "One of a kind"];
  return (
    <div className="overflow-hidden border-y py-5" style={{ borderColor: Z.line, background: Z.cream }} aria-hidden>
      <div className="flex w-max zn-scroll-slow">
        {[0, 1, 2].map((r) => <div key={r} className="flex shrink-0 items-center">{items.map((t) => (
          <span key={t + r} className="flex items-center gap-6 pr-6 text-[22px] md:text-[30px] italic whitespace-nowrap" style={{ fontFamily: Z.serif, color: Z.ink }}><Sparkles size={22} style={{ color: Z.primary }} className="zn-spin-slow" />{t}</span>
        ))}</div>)}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- shop the look */
function ShopLook({ s, cart, scenes, pool }) {
  const [i, setI] = useState(0);
  const [h, setH] = useState(0);
  const scene = scenes[i];
  const sibling = pool.find((p) => p.id !== scene.id && p.category === scene.category) || pool.find((p) => p.id !== scene.id) || scene;
  const spots = [{ x: "30%", y: "36%", p: scene }, { x: "66%", y: "64%", p: sibling }];
  const active = spots[h].p;
  const go = (d) => { setI((x) => (x + d + scenes.length) % scenes.length); setH(0); };
  const opts = needsOptions(active);
  const add = () => { if (opts) { window.location.href = `/store/${s.slug}/product/${active.id}`; return; } cart.add(active, null, 1); toast.success("Added to cart"); };
  return (
    <section id="shop-the-look" className={`${wrap} py-16 md:py-24`}>
      <Reveal><ZTitle eyebrow="Styled by our team">Shop the <em style={{ color: Z.primary }}>look</em></ZTitle></Reveal>
      <Reveal kind="rv-zoom" className="mt-12">
        <div className="relative grid lg:grid-cols-[1.35fr_1fr] gap-6 items-center rounded-[32px] p-4 md:p-6" style={{ background: Z.sand }}>
          <div className="relative aspect-[4/3] rounded-[24px] overflow-hidden" style={{ background: Z.cream }}>
            <div key={scene.id} className="absolute inset-0 zn-in"><ToneImg src={scene.image} alt={scene.name} loading="lazy" imgClassName="zn-kenburns" /></div>
            {spots.map((sp, k) => (
              <button key={k} type="button" aria-label={`Show ${sp.p.name}`} onClick={() => setH(k)} onMouseEnter={() => setH(k)} className="absolute -translate-x-1/2 -translate-y-1/2 w-9 h-9 rounded-full flex items-center justify-center" style={{ left: sp.x, top: sp.y }}>
                <span className="zn-ping absolute inset-0 rounded-full" style={{ background: "rgba(255,255,255,.7)" }} />
                <span className="relative w-5 h-5 rounded-full border-[3px] border-white shadow-lg transition-transform" style={{ background: h === k ? Z.primary : Z.ink, transform: h === k ? "scale(1.25)" : "none" }} />
              </button>
            ))}
            <div className="absolute left-4 bottom-4 flex gap-2">
              <button type="button" onClick={() => go(-1)} aria-label="Previous look" className="w-10 h-10 rounded-full bg-white/95 flex items-center justify-center shadow transition-transform hover:scale-110"><ArrowLeft size={16} /></button>
              <button type="button" onClick={() => go(1)} aria-label="Next look" className="w-10 h-10 rounded-full bg-white/95 flex items-center justify-center shadow transition-transform hover:scale-110"><ArrowRight size={16} /></button>
            </div>
          </div>
          <div key={active.id} className="zn-in rounded-[24px] bg-white p-5 md:p-6 max-w-md w-full mx-auto lg:mx-0" data-testid="artisan-look-card">
            <Link to={`/store/${s.slug}/product/${active.id}`} className="block aspect-[5/4] rounded-2xl overflow-hidden" style={{ background: Z.sand }}><ToneImg src={active.image} alt={active.name} loading="lazy" imgClassName="zn-zoom" /></Link>
            <h3 className="mt-4 text-[18px] leading-snug line-clamp-2" style={{ fontFamily: Z.serif, fontWeight: 500, color: Z.ink }}>{active.name}</h3>
            <div className="mt-1.5 flex items-baseline gap-2"><span className="text-[17px] font-semibold" style={{ color: Z.primary }}>{inr(active.price)}</span>{active.compare_at_price ? <span className="text-[13px] line-through" style={{ color: Z.muted }}>{inr(active.compare_at_price)}</span> : null}</div>
            <div className="mt-2"><Stars id={active.id} /></div>
            <div className="mt-5 grid gap-2.5">
              <button type="button" onClick={add} disabled={active.stock <= 0} className="zn-btn h-12 rounded-full text-[12px] font-semibold uppercase inline-flex items-center justify-center gap-2 disabled:opacity-50" style={{ letterSpacing: ".14em", background: Z.ink, color: "#fff" }}><ShoppingBag size={15} />{opts ? "Select options" : "Add to cart"}</button>
              <Link to={`/store/${s.slug}/product/${active.id}`} className="zn-btn h-12 rounded-full text-[12px] font-semibold uppercase inline-flex items-center justify-center border-[1.5px]" style={{ letterSpacing: ".14em", borderColor: Z.ink, color: Z.ink }}>View product</Link>
            </div>
            <div className="mt-4 flex justify-center gap-2">{scenes.map((p, k) => <button key={p.id} type="button" aria-label={`Look ${k + 1}`} onClick={() => { setI(k); setH(0); }} className="h-1.5 rounded-full transition-all" style={{ width: k === i ? 26 : 8, background: k === i ? Z.primary : "#d8cbb6" }} />)}</div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
